// frontend-booking/src/pages/BookingManagePage.js
// ============================================================
// 自助管理訂位（T-015，cancel-by-token）— 公開路由 /c/:token
//   查看訂位 → 改時間/人數（真改同一筆，重配桌）或 取消（釋放桌位）
// 免登入：token 即憑證。頂部沿用 Rebaz navbar。
// ============================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, Stack, CircularProgress, Alert, Paper, IconButton, Link
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LanguageIcon from '@mui/icons-material/Language';
import MapIcon from '@mui/icons-material/Map';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import bookingService from '../services/bookingService';
import rebazLogoR from '../assets/rebaz-logo-r.png';
import rebazLogoWords from '../assets/rebaz-logo-words.png';

const GREEN = '#06C755';
const GREEN_D = '#05b34c';
const BRAND_BLUE = '#145da0';

const pad2 = (n) => String(n).padStart(2, '0');
const todayTaipei = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' });
const addDaysISO = (iso, days) => {
  const d = new Date(`${iso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];
const dowOf = (iso) => new Date(`${iso}T00:00:00Z`).getUTCDay();
const niceDate = (iso, today) => {
  if (!iso) return '';
  const off = Math.round((new Date(`${iso}T00:00:00Z`) - new Date(`${today}T00:00:00Z`)) / 86400000);
  const [, m, d] = iso.split('-');
  const md = `${parseInt(m, 10)}/${parseInt(d, 10)}`;
  const wd = WEEKDAYS[dowOf(iso)];
  if (off === 0) return `今天 ${md}（週${wd}）`;
  if (off === 1) return `明天 ${md}（週${wd}）`;
  return `${md}（週${wd}）`;
};
// 月曆工具（與新訂位頁 BookingPage 一致）
const isoOf = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;              // m: 0-11
const monthDaysCount = (y, m) => new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
const firstWeekday = (y, m) => new Date(Date.UTC(y, m, 1)).getUTCDay();   // 0=Sun

const TopBar = () => (
  <Box sx={{
    position: 'sticky', top: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    px: { xs: 2, sm: 3 }, height: 52, bgcolor: '#fff', borderBottom: '1px solid', borderColor: 'divider'
  }}>
    <Link href="https://rebaz.ai" target="_blank" rel="noopener" underline="none" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box component="img" src={rebazLogoR} alt="Rebaz" sx={{ height: 30, width: 'auto' }} />
      <Box component="img" src={rebazLogoWords} alt="Rebaz" sx={{ height: 18, width: 'auto' }} />
    </Link>
    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>線上訂位</Typography>
  </Box>
);

const Page = ({ children }) => (
  <Box sx={{ minHeight: '100vh', bgcolor: '#eef1f5' }}>
    <TopBar />
    <Box sx={{ py: { xs: 0, sm: 4 } }}>
      <Box sx={{
        width: '100%', maxWidth: 600, mx: 'auto', bgcolor: '#fff', borderRadius: { xs: 0, sm: 3 },
        overflow: 'hidden', boxShadow: { xs: 'none', sm: '0 8px 30px rgba(0,0,0,.08)' }, minHeight: { xs: '100vh', sm: 'auto' }
      }}>
        {children}
      </Box>
    </Box>
  </Box>
);

const Row = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" sx={{ py: 0.9, fontSize: 15 }}>
    <Typography sx={{ fontSize: 15 }} color="text.secondary">{label}</Typography>
    <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{value}</Typography>
  </Stack>
);

const ERR = {
  FULL: '所選時段已無空桌，請換個時間',
  CLOSED: '所選時間非營業時段',
  CLOSED_DATE: '所選日期暫不開放訂位',
  PARTY_SIZE: '人數超出可訂範圍',
  OUT_OF_WINDOW: '所選日期不在可訂範圍',
  CANCELLED: '此訂位已取消，無法修改',
  SERVER_ERROR: '系統忙線中，請稍後再試'
};

const BookingManagePage = () => {
  const { token } = useParams();
  const today = todayTaipei();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [data, setData] = useState(null); // { reservation, restaurant, config, businessHours, weeklyClosedDays }
  const [view, setView] = useState('active'); // active | modify | cancelConfirm | canceled
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');

  // modify 狀態
  const [mDate, setMDate] = useState(null);
  const [mSlots, setMSlots] = useState([]);
  const [mSlotsLoading, setMSlotsLoading] = useState(false);
  const [mTime, setMTime] = useState(null);
  const [mParty, setMParty] = useState(2);
  // 月曆檢視月份（modify 改成月曆，與新訂位頁一致）
  const [viewY, setViewY] = useState(() => parseInt(todayTaipei().slice(0, 4), 10));
  const [viewM, setViewM] = useState(() => parseInt(todayTaipei().slice(5, 7), 10) - 1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await bookingService.getManage(token);
      if (d && d.success && d.reservation) {
        setData(d);
        if (d.reservation.status === 'cancelled') setView('canceled');
      } else setNotFound(true);
    } catch (e) { setNotFound(true); } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const r = data && data.reservation;
  const rest = data && data.restaurant;

  // 分頁標題：帶入餐廳名，離開時還原
  useEffect(() => {
    document.title = rest?.name ? `${rest.name} · 訂位管理` : 'Rebaz 線上訂位';
    return () => { document.title = 'Rebaz 線上訂位'; };
  }, [rest]);
  const cfg = (data && data.config) || {};
  const maxParty = cfg.maxPartySize || 10;
  const windowDays = cfg.bookingWindowDays || 90;
  const weeklyClosed = (data && data.weeklyClosedDays) || [];

  // 可選日期：今天起 windowDays 內、非固定公休（openModify 用來取第一個開放日當預設）
  const openDays = useMemo(() => {
    const out = [];
    for (let i = 0; i < windowDays && out.length < 30; i++) {
      const iso = addDaysISO(today, i);
      if (!weeklyClosed.includes(dowOf(iso))) out.push(iso);
    }
    return out;
  }, [today, windowDays, weeklyClosed]);

  // 月曆（可翻月、到 windowDays＝90 天；公休劃線）
  const maxDate = addDaysISO(today, windowDays);
  const curY = parseInt(today.slice(0, 4), 10);
  const curM = parseInt(today.slice(5, 7), 10) - 1;
  const calendarCells = useMemo(() => {
    const lead = firstWeekday(viewY, viewM);
    const n = monthDaysCount(viewY, viewM);
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= n; d++) cells.push(d);
    return cells;
  }, [viewY, viewM]);
  const prevDisabled = viewY < curY || (viewY === curY && viewM <= curM);
  const nmM = viewM === 11 ? 0 : viewM + 1;
  const nmY = viewM === 11 ? viewY + 1 : viewY;
  const nextDisabled = `${nmY}-${pad2(nmM + 1)}-01` > maxDate;
  const goPrev = () => { let m = viewM - 1, y = viewY; if (m < 0) { m = 11; y -= 1; } setViewM(m); setViewY(y); };
  const goNext = () => { let m = viewM + 1, y = viewY; if (m > 11) { m = 0; y += 1; } setViewM(m); setViewY(y); };

  const loadSlots = useCallback(async (date, party) => {
    if (!rest || !rest.slug) return;
    setMSlotsLoading(true); setMTime(null);
    try {
      const av = await bookingService.getAvailability(rest.slug, date, party);
      setMSlots(Array.isArray(av && av.slots) ? av.slots : []);
    } catch (e) { setMSlots([]); } finally { setMSlotsLoading(false); }
  }, [rest]);

  const openModify = () => {
    setErr('');
    const initDate = (r && !weeklyClosed.includes(dowOf(r.date))) ? r.date : (openDays[0] || today);
    setMParty(r ? r.partySize : 2);
    setMDate(initDate);
    setViewY(parseInt(initDate.slice(0, 4), 10));
    setViewM(parseInt(initDate.slice(5, 7), 10) - 1);
    setView('modify');
    loadSlots(initDate, r ? r.partySize : 2);
  };
  const pickDate = (iso) => { setMDate(iso); loadSlots(iso, mParty); };
  const setParty = (v) => { const p = Math.max(1, Math.min(v, maxParty)); setMParty(p); if (mDate) loadSlots(mDate, p); };

  const doModify = async () => {
    if (!mDate || !mTime) { setErr('請選擇新的日期與時段'); return; }
    setBusy(true); setErr('');
    try {
      const res = await bookingService.modifyManage(token, { date: mDate, time: mTime, partySize: mParty });
      if (res && res.success) {
        setData((prev) => ({ ...prev, reservation: { ...prev.reservation, date: res.reservation.date, time: res.reservation.time, partySize: res.reservation.partySize } }));
        setView('active'); setToast('訂位已更新 ✓');
        setTimeout(() => setToast(''), 2200);
      }
    } catch (e) {
      const code = e && e.response && e.response.data && e.response.data.error;
      setErr(ERR[code] || '修改失敗，請稍後再試');
      if (code === 'FULL') loadSlots(mDate, mParty);
    } finally { setBusy(false); }
  };

  const doCancel = async () => {
    setBusy(true); setErr('');
    try {
      await bookingService.cancelManage(token);
      setData((prev) => ({ ...prev, reservation: { ...prev.reservation, status: 'cancelled' } }));
      setView('canceled');
    } catch (e) { setErr('取消失敗，請稍後再試'); } finally { setBusy(false); }
  };

  if (loading) {
    return <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#eef1f5' }}><CircularProgress sx={{ color: GREEN }} /></Box>;
  }
  if (notFound) {
    return (
      <Page><Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>找不到這筆訂位</Typography>
        <Typography sx={{ fontSize: 15 }} color="text.secondary">連結可能不正確或已失效。如需協助，請直接與餐廳聯繫。</Typography>
      </Box></Page>
    );
  }

  const cancelled = r && r.status === 'cancelled';

  return (
    <Page>
      <Box sx={{ p: { xs: 3, sm: '28px 36px 40px' } }}>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>管理訂位</Typography>

        {/* 餐廳資訊 + 地圖（保留；改期時仍在上方） */}
        <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 2, mb: 1.5 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 800 }}>{rest && rest.name}</Typography>
          {rest && rest.address && <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 1.2 }}>{rest.address}</Typography>}
          {rest && rest.address && (
            <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', mb: 1.2 }}>
              <Box component="iframe" title="餐廳位置地圖" loading="lazy"
                src={`https://www.google.com/maps?q=${encodeURIComponent(rest.address)}&output=embed`}
                sx={{ width: '100%', height: 160, border: 0, display: 'block' }} />
            </Box>
          )}
          {rest && (rest.website || rest.mapUrl) && (
            <Stack direction="row" spacing={2}>
              {rest.website && (
                <Link href={rest.website} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 14, color: BRAND_BLUE }}>
                  <LanguageIcon sx={{ fontSize: 18 }} />官方網站</Link>
              )}
              {rest.mapUrl && (
                <Link href={rest.mapUrl} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 14, color: BRAND_BLUE }}>
                  <MapIcon sx={{ fontSize: 18 }} />Google Maps 導航</Link>
              )}
            </Stack>
          )}
        </Paper>

        {/* 訂位詳情（含訂位人 / 電話） */}
        <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800 }}>訂位詳情</Typography>
            <Chip size="small" label={cancelled ? '已取消' : '已確認'}
              sx={{ flex: 'none', fontWeight: 700, bgcolor: cancelled ? '#fdecec' : 'rgba(6,199,85,.12)', color: cancelled ? '#d64545' : GREEN_D }} />
          </Stack>
          {r.name && <Row label="訂位人" value={r.name} />}
          {r.phone && <Row label="電話" value={r.phone} />}
          <Row label="日期時間" value={`${niceDate(r.date, today)} · ${r.time}`} />
          <Row label="人數" value={`${r.partySize} 位`} />
          <Row label="訂位編號" value={`#${String(r.id).slice(0, 8).toUpperCase()}`} />
        </Paper>

        {toast && <Alert severity="success" sx={{ mt: 2, borderRadius: 2, fontSize: 15 }}>{toast}</Alert>}

        {cancelled ? (
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography sx={{ fontSize: 40 }}>🗑️</Typography>
            <Typography sx={{ fontSize: 17, fontWeight: 700, mt: 1 }}>訂位已取消</Typography>
            <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 2 }}>桌位已釋出。歡迎隨時再次預訂。</Typography>
            {rest && rest.slug && (
              <Button variant="contained" component="a" href={`/r/${rest.slug}`}
                sx={{ bgcolor: BRAND_BLUE, '&:hover': { bgcolor: BRAND_BLUE }, fontSize: 15 }}>重新預訂</Button>
            )}
          </Box>
        ) : view === 'active' ? (
          <Stack spacing={1.2} sx={{ mt: 2 }}>
            <Button fullWidth variant="outlined" onClick={openModify}
              sx={{ color: GREEN_D, borderColor: GREEN, fontSize: 15, py: 1.3, '&:hover': { borderColor: GREEN_D } }}>🕒 改時間 / 人數</Button>
            <Button fullWidth variant="outlined" onClick={() => { setErr(''); setView('cancelConfirm'); }}
              sx={{ color: '#d64545', borderColor: '#f0b4b4', fontSize: 15, py: 1.3, '&:hover': { borderColor: '#d64545', bgcolor: '#fdecec' } }}>取消訂位</Button>
          </Stack>
        ) : view === 'cancelConfirm' ? (
          <Box sx={{ mt: 2, border: '1px solid #f0c2c2', bgcolor: '#fdecec', borderRadius: 2, p: 2 }}>
            <Typography sx={{ fontSize: 14, color: '#8a3a3a', mb: 1.5 }}>確定要取消這筆訂位嗎？取消後桌位會釋出，此動作無法復原。</Typography>
            <Stack direction="row" spacing={1.2}>
              <Button variant="outlined" onClick={doCancel} disabled={busy}
                sx={{ color: '#d64545', borderColor: '#f0b4b4', fontSize: 15 }}>{busy ? <CircularProgress size={18} /> : '確定取消'}</Button>
              <Button variant="text" onClick={() => setView('active')} sx={{ color: 'text.secondary', fontSize: 15 }}>先不要</Button>
            </Stack>
          </Box>
        ) : (
          /* view === 'modify' */
          <Box sx={{ mt: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: '#fbfcfe' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary', mb: 1 }}>選擇新日期</Typography>
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Button size="small" disabled={prevDisabled} onClick={goPrev}
                  sx={{ color: 'text.primary', fontSize: 14, textTransform: 'none', minWidth: 0, px: 0.5 }}>‹ 上個月</Button>
                <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{viewY} 年 {viewM + 1} 月</Typography>
                <Button size="small" disabled={nextDisabled} onClick={goNext}
                  sx={{ color: 'text.primary', fontSize: 14, textTransform: 'none', minWidth: 0, px: 0.5 }}>下個月 ›</Button>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', mb: 0.5 }}>
                {WEEKDAYS.map((w) => <Typography key={w} sx={{ fontSize: 13 }} color="text.secondary">{w}</Typography>)}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                {calendarCells.map((cell, i) => {
                  if (cell === null) return <Box key={`e${i}`} sx={{ height: 38 }} />;
                  const iso = isoOf(viewY, viewM, cell);
                  const closedDay = weeklyClosed.includes(dowOf(iso));
                  const selectable = iso >= today && iso <= maxDate && !closedDay;
                  const sel = iso === mDate && selectable;
                  return (
                    <Box key={iso} onClick={() => selectable && pickDate(iso)}
                      sx={{
                        height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: 2, fontSize: 15, cursor: selectable ? 'pointer' : 'default',
                        color: sel ? '#fff' : (selectable ? 'text.primary' : 'text.disabled'),
                        textDecoration: closedDay ? 'line-through' : 'none',
                        bgcolor: sel ? GREEN : 'transparent',
                        '&:hover': selectable && !sel ? { bgcolor: '#f0f0f0' } : {}
                      }}>
                      {cell}
                    </Box>
                  );
                })}
              </Box>
              {weeklyClosed.length > 0 && (
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>
                  每週{weeklyClosed.map((d) => `週${WEEKDAYS[d]}`).join('、')}公休
                </Typography>
              )}
            </Paper>

            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary', mb: 1 }}>選擇新時段</Typography>
            {mSlotsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} sx={{ color: GREEN }} /></Box>
            ) : mSlots.length === 0 || mSlots.every((s) => !s.available) ? (
              <Typography sx={{ fontSize: 14, color: 'text.secondary', py: 1 }}>本日無可訂時段，請換一天</Typography>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mb: 1.5 }}>
                {mSlots.map((s) => {
                  const on = s.time === mTime;
                  return (
                    <Chip key={s.time} label={s.time} disabled={!s.available} onClick={() => s.available && setMTime(s.time)}
                      sx={{ height: 42, borderRadius: 2, fontSize: 15, textDecoration: s.available ? 'none' : 'line-through',
                        bgcolor: on ? GREEN : '#fff', color: on ? '#fff' : 'text.primary', border: '1px solid', borderColor: on ? GREEN : 'divider' }} />
                  );
                })}
              </Box>
            )}

            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary', mb: 1, mt: 1 }}>用餐人數</Typography>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <IconButton size="small" disabled={mParty <= 1} onClick={() => setParty(mParty - 1)} sx={{ border: '1px solid', borderColor: 'divider' }}><RemoveIcon fontSize="small" /></IconButton>
              <Typography sx={{ minWidth: 30, textAlign: 'center', fontSize: 19, fontWeight: 700 }}>{mParty}</Typography>
              <IconButton size="small" disabled={mParty >= maxParty} onClick={() => setParty(mParty + 1)} sx={{ border: '1px solid', borderColor: 'divider' }}><AddIcon fontSize="small" /></IconButton>
              <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>位（每筆最多 {maxParty} 位）</Typography>
            </Stack>

            {err && <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2, fontSize: 14 }}>{err}</Alert>}
            <Stack direction="row" spacing={1.2}>
              <Button variant="contained" onClick={doModify} disabled={busy || !mTime}
                sx={{ bgcolor: GREEN, '&:hover': { bgcolor: GREEN_D }, fontSize: 15, flex: 1 }}>{busy ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : '確認修改'}</Button>
              <Button variant="text" onClick={() => { setView('active'); setErr(''); }} sx={{ color: 'text.secondary', fontSize: 15 }}>返回</Button>
            </Stack>
            <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 1.2 }}>修改會即時重新確認空位（公休 / 營業時段 / 容量都會檢查）。</Typography>
          </Box>
        )}

        {err && view === 'active' && <Alert severity="error" sx={{ mt: 2, borderRadius: 2, fontSize: 14 }}>{err}</Alert>}

        {/* 最下方：加入餐廳官方 LINE（純按鈕，無說明文字；後台可設 enabled+url）*/}
        {rest?.preorderLine?.enabled && rest?.preorderLine?.url && (
          <Button fullWidth variant="contained" component="a" href={rest.preorderLine.url} target="_blank" rel="noopener"
            startIcon={<RestaurantMenuIcon />}
            sx={{ mt: 2, bgcolor: GREEN, '&:hover': { bgcolor: GREEN_D }, fontSize: 16, fontWeight: 700, py: 1.2 }}>
            加入官方帳號先點菜
          </Button>
        )}
      </Box>
    </Page>
  );
};

export default BookingManagePage;
